import {
    Color3,
    Constants,
    Vector3,
    Vector4,
    type Mesh,
    type Scene,
    type ShaderMaterial,
} from '@babylonjs/core';
import { buildHexWardGeometry, HEX_WARD_EDGE_KIND, HEX_WARD_TILE_KIND } from './hex-ward-geometry';
import type { BodyBounds } from './body-bounds';
import { HEX_WARD_MAX_HITS, registerHexWardShaders } from './hex-ward-shaders';
import { createVfxShaderMaterial } from './vfx-shader-material';

/**
 * - `ripple`: the shell is invisible at rest and only shows where hits land
 *   (the Warded elite).
 * - `shell`: the whole shell shows, assembling from the ground up and
 *   breaking into falling tiles at the end (the Warding crystal's dome).
 */
export type HexWardMode = 'ripple' | 'shell';

export interface HexWardOptions {
    /** The shell's center, in scene space. A grounded dome's center sits on the floor. */
    readonly position: Vector3;
    readonly radius: number;
    readonly colorHex: string;
    readonly mode: HexWardMode;
    /** Cuts away the shell below its center and burns its base bright, for a dome standing on the floor. */
    readonly grounded?: boolean;
    /** A shell starts built instead of assembling. Ignored for `ripple`, which is always built. */
    readonly isAssembled?: boolean;
}

export interface HexWardSystemOptions {
    /** Goldberg `m`: the shell has 10m^2 + 2 tiles. */
    readonly tileDensity?: number;
}

const DEFAULT_TILE_DENSITY = 5;
/** A body's ward stands this much clear of its largest half-extent, so it wraps the body rather than cutting through it. */
export const HEX_WARD_BODY_MARGIN = 1.1;
/** How long a shell takes to assemble from the ground up. */
const ASSEMBLE_SECONDS = 0.7;
/** How long a shell takes to break apart and drop its tiles. */
const SHATTER_SECONDS = 1.2;
/** How long one hit's ripple runs before its slot frees up. */
const HIT_LIFETIME_SECONDS = 1.4;
const EMPTY_HIT = new Vector4(0, 1, 0, -1);

type WardPhase = 'assembling' | 'standing' | 'shattering' | 'gone';

interface WardHit {
    readonly direction: Vector3;
    ageSeconds: number;
}

interface DrawnWard {
    readonly mesh: Mesh;
    readonly material: ShaderMaterial;
    readonly mode: HexWardMode;
    readonly hits: WardHit[];
    phase: WardPhase;
    assembly: number;
    breakup: number;
}

/** Where a ward around a body sits, and how big it is. */
export interface HexWardFit {
    readonly center: Vector3;
    readonly radius: number;
}

/**
 * A ward that fits around a body (`measureBody`): centered on it, as big as
 * its half-height or half-length, whichever is greater, with a margin, so a
 * small creature and a towering one are each wrapped close. Its width isn't
 * used: a skinned body measures in its bind pose, arms out, so its width is
 * its arm span, far wider than it stands.
 */
export function wardAroundBody(bounds: BodyBounds): HexWardFit {
    const half = bounds.max.subtract(bounds.min).scaleInPlace(0.5);
    return { center: bounds.center.clone(), radius: Math.max(half.y, half.z) * HEX_WARD_BODY_MARGIN };
}

/**
 * A grounded dome over a body (`measureBody`): standing on the floor under
 * it, `minRadius` across, or taller to stand over a taller body.
 */
export function domeOverBody(bounds: BodyBounds, minRadius: number): HexWardFit {
    const height = bounds.max.y - bounds.min.y;
    return {
        center: new Vector3(bounds.center.x, bounds.min.y, bounds.center.z),
        radius: Math.max(minRadius, height * HEX_WARD_BODY_MARGIN),
    };
}

/**
 * Draws hexagonal wards: tiled shells around a character that show a ward
 * stopping damage (docs/gameplay/combat-vfx.md, "Shared pieces"). Every ward
 * shares one tiled shell geometry; each has its own material, since its
 * color, hits and assembly state are its own. A shattered shell is kept,
 * hidden, so `assemble` can build it again; its owner frees it with
 * `removeWard`.
 */
export class HexWardSystem {
    private readonly template: Mesh;
    private readonly wards = new Map<string, DrawnWard>();
    private timeSeconds = 0;

    constructor(private readonly scene: Scene, options: HexWardSystemOptions = {}) {
        registerHexWardShaders();
        this.template = buildHexWardGeometry(scene, 'hex-ward-template', options.tileDensity ?? DEFAULT_TILE_DENSITY).mesh;
        this.template.setEnabled(false);
    }

    createWard(id: string, options: HexWardOptions): void {
        this.removeWard(id);

        const mesh = this.template.clone(`hex-ward-${id}`);
        mesh.setEnabled(true);
        mesh.position.copyFrom(options.position);
        mesh.scaling.setAll(options.radius);
        mesh.isPickable = false;

        const material = createVfxShaderMaterial(this.scene, `hex-ward-${id}`, 'hexWard', {
            attributes: ['position', 'normal', HEX_WARD_EDGE_KIND, HEX_WARD_TILE_KIND],
            uniforms: [
                    'world', 'viewProjection', 'cameraPosition', 'time', 'assembly', 'breakup',
                    'wardColor', 'surface', 'intensity', 'groundClip', 'hit0', 'hit1', 'hit2', 'hit3',
                ],
            alphaMode: Constants.ALPHA_ADD,
        });
        material.setColor3('wardColor', Color3.FromHexString(options.colorHex));
        material.setFloat('surface', options.mode === 'shell' ? 1 : 0);
        material.setFloat('intensity', 1);
        material.setFloat('groundClip', options.grounded ? 1 : 0);
        mesh.material = material;

        const startsBuilt = options.mode === 'ripple' || options.isAssembled === true;
        const ward: DrawnWard = {
            mesh,
            material,
            mode: options.mode,
            hits: [],
            phase: startsBuilt ? 'standing' : 'assembling',
            assembly: startsBuilt ? 1 : 0,
            breakup: 0,
        };
        this.wards.set(id, ward);
        this.applyUniforms(ward);
    }

    setWardPosition(id: string, position: Vector3): void {
        this.wards.get(id)?.mesh.position.copyFrom(position);
    }

    /** Moves and sizes a ward to fit around a body (`wardAroundBody`): call as the body moves, or its model loads. */
    fitWardToBody(id: string, bounds: BodyBounds): HexWardFit {
        return this.placeWard(id, wardAroundBody(bounds));
    }

    /** Moves and sizes a ward to `fit`: a dome over a body (`domeOverBody`), say. */
    placeWard(id: string, fit: HexWardFit): HexWardFit {
        const ward = this.wards.get(id);
        ward?.mesh.position.copyFrom(fit.center);
        ward?.mesh.scaling.setAll(fit.radius);
        return fit;
    }

    /** A hit landed at `worldPoint`: a ripple races across the shell from the nearest point on it. */
    hit(id: string, worldPoint: Vector3): void {
        const ward = this.wards.get(id);
        if (!ward || ward.phase === 'gone') {
            return;
        }
        const offset = worldPoint.subtract(ward.mesh.position);
        const direction = offset.lengthSquared() > 1e-8 ? offset.normalize() : Vector3.Up();
        if (ward.hits.length >= HEX_WARD_MAX_HITS) {
            ward.hits.shift();
        }
        ward.hits.push({ direction, ageSeconds: 0 });
    }

    /** Builds a shell from the ground up, starting over if it was standing or breaking. */
    assemble(id: string): void {
        const ward = this.wards.get(id);
        if (!ward || ward.mode !== 'shell') {
            return;
        }
        ward.phase = 'assembling';
        ward.assembly = 0;
        ward.breakup = 0;
        ward.mesh.setEnabled(true);
    }

    /** Breaks a shell apart: its tiles let go, pop outward and fall. */
    shatter(id: string): void {
        const ward = this.wards.get(id);
        if (!ward || ward.mode !== 'shell' || ward.phase === 'gone') {
            return;
        }
        ward.phase = 'shattering';
    }

    removeWard(id: string): void {
        const ward = this.wards.get(id);
        if (!ward) {
            return;
        }
        ward.mesh.dispose();
        ward.material.dispose();
        this.wards.delete(id);
    }

    update(deltaSeconds: number): void {
        this.timeSeconds += deltaSeconds;
        for (const ward of this.wards.values()) {
            this.advance(ward, deltaSeconds);
            this.applyUniforms(ward);
        }
    }

    dispose(): void {
        for (const id of [...this.wards.keys()]) {
            this.removeWard(id);
        }
        this.template.dispose();
    }

    private advance(ward: DrawnWard, deltaSeconds: number): void {
        for (const hit of ward.hits) {
            hit.ageSeconds += deltaSeconds;
        }
        while (ward.hits.length > 0 && ward.hits[0]!.ageSeconds > HIT_LIFETIME_SECONDS) {
            ward.hits.shift();
        }

        if (ward.phase === 'assembling') {
            ward.assembly = Math.min(1, ward.assembly + (deltaSeconds / ASSEMBLE_SECONDS));
            if (ward.assembly >= 1) {
                ward.phase = 'standing';
            }
        } else if (ward.phase === 'shattering') {
            ward.breakup = Math.min(1, ward.breakup + (deltaSeconds / SHATTER_SECONDS));
            if (ward.breakup >= 1) {
                ward.phase = 'gone';
                ward.hits.length = 0;
                ward.mesh.setEnabled(false);
            }
        }
    }

    private applyUniforms(ward: DrawnWard): void {
        const { material } = ward;
        material.setFloat('time', this.timeSeconds);
        material.setFloat('assembly', ward.assembly);
        material.setFloat('breakup', ward.breakup);
        for (let slot = 0; slot < HEX_WARD_MAX_HITS; slot++) {
            const hit = ward.hits[slot];
            material.setVector4(`hit${slot}`, hit
                ? new Vector4(hit.direction.x, hit.direction.y, hit.direction.z, hit.ageSeconds)
                : EMPTY_HIT);
        }
    }
}
